import { useState } from 'react';
import { connect } from 'react-redux';
import { AnyAction, bindActionCreators, Dispatch } from 'redux';
import { FormattedMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBriefcaseClock, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { formatMessage } from 'i18n/CurrentLocale';
import { Company, EditableCompanyDetails, ReduxState } from 'types';
import { AlertBox } from 'components/AlertBox';
import * as companyActions from 'features/company/companyActions';
import * as modalWindowActions from 'root/modal/modalWindowActions';
import moment, { Moment } from 'moment';
import ErrorMessageBox from 'components/ErrorMessageBox';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { FormSource } from 'features/company/companyDetails';


export const validateThatEndTimeIsGreaterThanStartTime = (startTime: Moment | null, endTime: Moment | null): boolean => {
    return !!(startTime && endTime && endTime?.isAfter(startTime));
}

export const getEditableCompanyDetails = (companyDetails: Company): EditableCompanyDetails => ({
    name: companyDetails.name,
    vat: companyDetails.vat,
    address: {
        street: companyDetails?.address?.street,
        number: companyDetails?.address?.number,
        city: companyDetails?.address?.city,
        postalCode: companyDetails?.address?.postalCode,
        countryCode: companyDetails?.address?.countryCode,
    },
    email: companyDetails?.email,
    workingHoursStart: companyDetails?.workingHoursStart,
    workingHoursEnd: companyDetails?.workingHoursEnd
});


export const formatTimeAsHHMM = (time: string): string => {
    return `${time.slice(0, 5)}`;
}

export const formatTimeAsHHMMSSsss = (time: string): string => {
    return `${time.slice(0, 5)}:00.000`;
}

export const convertTimeToMomentTime = (timeAsHHMMSSsss: string | null): Moment | null => {
    return timeAsHHMMSSsss
        ? moment(`2000-01-01 ${timeAsHHMMSSsss}`, 'YYYY-MM-DD hh:mm:ss')
        : null
}

export const convertMomentToHHMMSSsss = (time: Moment): string => {
    const hoursLeadingZero = String(time.get('hours')).padStart(2, '0');
    const minutesLeadingZero = String(time.get('minutes')).padStart(2, '0');

    return `${hoursLeadingZero}:${minutesLeadingZero}:00.000`;
}

export const EditCompanyWorkingHoursModal = (props: Props) => {
    const { workingHoursStart, workingHoursEnd } = props.companyDetails?.companyDetails;
    const [startTime, setStartTime] = useState<Moment | null>(convertTimeToMomentTime(workingHoursStart));
    const [endTime, setEndTime] = useState<Moment | null>(convertTimeToMomentTime(workingHoursEnd));

    const saveWorkingHours = (e: any) => {
        const newCompanyDetails = {
            ...getEditableCompanyDetails(props.companyDetails?.companyDetails),
            workingHoursStart: convertMomentToHHMMSSsss(startTime!),
            workingHoursEnd: convertMomentToHHMMSSsss(endTime!)
        }
        props.updateCompanyDetails(newCompanyDetails, FormSource.UPDATE_WORKING_HOURS);
        props.closeModal();
    }


    const isEmpty = (startTime === null || endTime === null);
    const endTimeIsGreaterThanStartTime = validateThatEndTimeIsGreaterThanStartTime(startTime, endTime);

    const isValid = !isEmpty && endTimeIsGreaterThanStartTime;

    return (
        <div className="panel medium radius-rectangle full-width-form confirmation-modal">
            <div className="panel-header">
                <h2 className="bold-title">
                    <FontAwesomeIcon icon={faBriefcaseClock} size='sm' /><FormattedMessage id="SETTINGS_WH_MODAL_TITLE" />
                </h2>
                <button className="btn-close-modal" onClick={() => props.closeModal()} />
            </div>

            <div className="panel-content align-left">
                <LocalizationProvider dateAdapter={AdapterMoment}>
                    <div className="grid-2-col-gap">
                        <div id="timepicker-starttime-wrapper" data-testid="timepicker-starttime-wrapper">
                            <TimePicker
                                label={formatMessage({ id: 'SETTINGS_WH_MODAL_LABEL_START' })}
                                value={startTime}
                                onChange={(newTime) => setStartTime(newTime)}
                                views={['hours', 'minutes']}
                                format='HH:mm'
                                ampm={false}
                                minutesStep={1}
                                timeSteps={{ hours: 1, minutes: 15 }}
                                slotProps={{
                                    textField: { fullWidth: true }
                                }}
                                className='time-picker-custom-css wh-input-start-time'
                            />
                        </div>
                        <div id="timepicker-endtime-wrapper" date-testid="timepicker-endtime-wrapper">
                            <TimePicker
                                label={formatMessage({ id: 'SETTINGS_WH_MODAL_LABEL_END' })}
                                value={endTime}
                                onChange={(newTime) => setEndTime(newTime)}
                                views={['hours', 'minutes']}
                                format='HH:mm'
                                ampm={false}
                                minutesStep={1}
                                timeSteps={{ hours: 1, minutes: 15 }}
                                slotProps={{
                                    textField: { fullWidth: true }
                                }}
                                className='time-picker-custom-css wh-input-end-time'
                            />
                        </div>
                    </div>
                </LocalizationProvider>


                {!endTimeIsGreaterThanStartTime && !isEmpty &&
                    <ErrorMessageBox errorMessage={formatMessage({ id: 'SETTINGS_WH_MODAL_VALIDATION_ENDTIME_BEFORE_STARTTIME' })} style={{ marginTop: '20px' }} />
                }

                <p style={{ marginTop: '20px' }}>
                    <FormattedMessage id='SETTINGS_WH_MODAL_APPLIED_DAYS_OF_WEEK' />
                </p>

                <AlertBox type='warn' icon={faExclamationTriangle} style={{ marginTop: '20px' }}>
                    <FormattedMessage id='SETTINGS_WH_DESCRIPTION_NEW_PARKING_SESSIONS' />
                    &nbsp;
                    <FormattedMessage id='SETTINGS_WH_DESCRIPTION_ONGOING_PARKING_SESSIONS' />
                </AlertBox>

                <button disabled={!isValid} onClick={saveWorkingHours} className='btn-blue'>{formatMessage({ id: 'SETTINGS_WH_MODAL_BTN_SAVE' })}</button>

            </div>
        </div>
    );
};


type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = StateProps & DispatchProps;

function mapStateToProps(state: ReduxState) {
    return {
        companyDetails: state.companyDetails
    };
}

function mapDispatchToProps(dispatch: Dispatch<AnyAction>) {
    return bindActionCreators({
        updateCompanyDetails: companyActions.updateCompanyDetails,
        closeModal: modalWindowActions.closeModal,
    }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(EditCompanyWorkingHoursModal);